/**
 * Textarea — a multi-line text field with the same label / hint / error
 * envelope as TextInput. Vertically resizable by default.
 */
import { forwardRef, useId, type ReactNode, type TextareaHTMLAttributes } from "react";

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Visible label above the field. */
  readonly label?: ReactNode;
  /** Helper text under the field (hidden when an error is shown). */
  readonly hint?: ReactNode;
  /** Error message — when set, the field renders its invalid state. */
  readonly error?: string;
  /** Marks the field required (adds a rose asterisk to the label). */
  readonly required?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  function Textarea(
    { label, hint, error, required, id, className, rows = 4, ...rest },
    ref,
  ) {
    const autoId = useId();
    const fieldId = id ?? autoId;
    const hintId = `${fieldId}-hint`;
    const errorId = `${fieldId}-error`;
    const invalid = Boolean(error);
    const describedBy = invalid ? errorId : hint != null ? hintId : undefined;

    return (
      <div className={["lc-field", className].filter(Boolean).join(" ")}>
        {label != null ? (
          <label className="lc-field__label" htmlFor={fieldId}>
            {label}
            {required ? <span className="lc-field__required" aria-hidden="true">*</span> : null}
          </label>
        ) : null}
        <textarea
          ref={ref}
          id={fieldId}
          rows={rows}
          className="lc-textarea"
          data-invalid={invalid || undefined}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          aria-required={required || undefined}
          required={required}
          {...rest}
        />
        {invalid ? (
          <span id={errorId} className="lc-field__error" role="alert">
            {error}
          </span>
        ) : hint != null ? (
          <span id={hintId} className="lc-field__hint">
            {hint}
          </span>
        ) : null}
      </div>
    );
  },
);
